import React, { useEffect, useRef, useState } from "react";

interface SearchableSelectProps {
  options: string[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  id?: string;
  className?: string;
  width?: number | string;
}

export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = "Selecione",
  searchPlaceholder = "Pesquisar...",
  id,
  className,
  width = 250,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = options.filter((o) =>
    o.toLowerCase().includes(query.toLowerCase())
  );

  const handleSelect = (option: string) => {
    onChange(option);
    setIsOpen(false);
    setQuery("");
  };

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
    setQuery("");
  };

  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const baseClass =
    className ??
    "bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5";

  return (
    <div
      ref={containerRef}
      id={id}
      style={{ position: "relative", width, flexShrink: 0 }}
    >
      <button
        type="button"
        onClick={handleToggle}
        className={baseClass}
        style={{
          textAlign: "left",
          cursor: "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {value || placeholder}
        </span>
        <span
          style={{
            marginLeft: 8,
            flexShrink: 0,
            display: "inline-flex",
            transition: "transform 0.2s",
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M4 6L8 10L12 6"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>

      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            zIndex: 9999,
            background: "#fff",
            border: "1px solid #d1d5db",
            borderRadius: 8,
            boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
            overflow: "hidden",
          }}
        >
          <div style={{ padding: "8px 8px 4px" }}>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              style={{
                width: "100%",
                padding: "6px 10px",
                fontSize: 13,
                border: "1px solid #d1d5db",
                borderRadius: 6,
                outline: "none",
                boxSizing: "border-box",
              }}
            />
          </div>
          <ul
            style={{
              listStyle: "none",
              margin: 0,
              padding: "4px 0",
              maxHeight: 220,
              overflowY: "auto",
            }}
          >
            <li
              onMouseDown={() => handleSelect("")}
              style={{
                padding: "8px 14px",
                fontSize: 13,
                cursor: "pointer",
                color: "#6b7280",
                background: value === "" ? "#f3f4f6" : "transparent",
              }}
              onMouseEnter={(e) =>
                ((e.currentTarget as HTMLLIElement).style.background =
                  "#f3f4f6")
              }
              onMouseLeave={(e) =>
                ((e.currentTarget as HTMLLIElement).style.background =
                  value === "" ? "#f3f4f6" : "transparent")
              }
            >
              {placeholder}
            </li>
            {filtered.length === 0 ? (
              <li
                style={{ padding: "8px 14px", fontSize: 13, color: "#9ca3af" }}
              >
                Nenhum resultado
              </li>
            ) : (
              filtered.map((option, index) => (
                <li
                  key={index}
                  onMouseDown={() => handleSelect(option)}
                  style={{
                    padding: "8px 14px",
                    fontSize: 13,
                    cursor: "pointer",
                    background: value === option ? "#eff6ff" : "transparent",
                    color: value === option ? "#1d4ed8" : "#111827",
                    fontWeight: value === option ? 500 : 400,
                  }}
                  onMouseEnter={(e) => {
                    if (value !== option)
                      (e.currentTarget as HTMLLIElement).style.background =
                        "#f3f4f6";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLLIElement).style.background =
                      value === option ? "#eff6ff" : "transparent";
                  }}
                >
                  {option}
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
};
